import { Component, OnInit, Input, SimpleChanges, ViewChild, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonModal, IonContent, IonGrid, IonRow, IonCol, IonSpinner, IonIcon, ToastController, AlertController
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { MarvDateService, MarvModalService } from '@marv/services';
import { 
    MarvTabsComponent, MarvTabComponent, MarvTab, MarvTagComponent, MarvButtonComponent, MarvTextareaComponent, 
    MarvInputComponent, MarvMessageComponent, MarvSubItemComponent, MarvDrawerComponent, MarvCheckboxComponent
} from '@marv/components';
import { 
    RetailerLogoComponent, RetailerLogo, MissionItem, MissionDetails, MissionDetailsInfosComponent, 
    MissionDetailsActivitiesComponent, MissionDetailsRatingComponent, MissionAddDayComponent, OrderDay,
    MissionDetailsAdditionalDataComponent, PermanentPlanningStorecheckComponent, PermanentPlanningStorecheckDuplicateComponent,
    StoreCheckChangeEvent
} from '@app/components';
import { OrderService, StoreCheckService, MissionTypeService, OperationService, LoggedUserService } from '@app/services';
import { Order, StoreCheck, AdditionalTimes, Operation, OperationCustomerPlanningSetting, OperationCard } from '@app/models';
import { EpochToStringPipe } from '@app/pipes';

export type MissionDetailsTab = 'infos'|'additional-data'|'activities'|'rating';

@Component({
    selector: 'app-mission-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    imports: [
        IonModal, IonContent, IonGrid, IonRow, IonCol, IonSpinner, IonIcon, TranslatePipe, ReactiveFormsModule, EpochToStringPipe,
        forwardRef(() => MarvTabsComponent), forwardRef(() => MarvTabComponent),  forwardRef(() => MarvTagComponent), forwardRef(() => MarvButtonComponent),
        forwardRef(() => MarvMessageComponent), forwardRef(() => MarvSubItemComponent), forwardRef(() => MarvInputComponent),
        forwardRef(() => MarvTextareaComponent), forwardRef(() => MarvDrawerComponent), forwardRef(() => MarvCheckboxComponent), 
        forwardRef(() => RetailerLogoComponent), forwardRef(() => MissionDetailsInfosComponent), forwardRef(() => MissionDetailsActivitiesComponent), 
        forwardRef(() => MissionDetailsRatingComponent), forwardRef(() => MissionAddDayComponent), forwardRef(() => MissionDetailsAdditionalDataComponent),
        forwardRef(() => PermanentPlanningStorecheckComponent), forwardRef(() => PermanentPlanningStorecheckDuplicateComponent)        
    ]
})
export class MissionDetailsComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'missionId', required : true}) missionId: number;
    @Input({alias : 'activityId', required : false}) activityId: number|null = null;
    @Input({alias : 'source', required : true}) source: 'loga'|'lgg';
    @Input({alias : 'tabSelected', required : false}) tabSelected: MissionDetailsTab = 'infos';
    @Output() onUpdateMission = new EventEmitter<boolean>();
    @Output() onDeleteMission = new EventEmitter<boolean>();
    
    @ViewChild('drawerEditDay') drawerEditDay: MarvDrawerComponent;
    @ViewChild('drawerPermanentStoreCheck') drawerPermanentStoreCheck: MarvDrawerComponent;    
    @ViewChild('drawerPermanentStoreCheckDuplicate') drawerPermanentStoreCheckDuplicate: MarvDrawerComponent;
    @ViewChild('modalDeleteOrder') modalDeleteOrder: IonModal;
    @ViewChild('modalRequestCancellation') modalRequestCancellation: IonModal;
    @ViewChild('modalAdditionalTime') modalAdditionalTime: IonModal;
    @ViewChild(MarvTabsComponent) marvTabs: MarvTabsComponent
    
    tabsMission: Array<MarvTab> = [
        {id: 'infos', label: this.translateService.instant('INFORMATIONS'), defaultSelected: true},
        // A MODIFIER - Ajouter dans l'API : Ajout des questionnaires de prise de commande + affichage du dernier questionnaire saisi (par OP)      
        {id: 'additional-data', label: this.translateService.instant('ADDITIONAL_DATA'), hide: true},
        {id: 'activities', label: this.translateService.instant('MISSIONS'), countItems: 0},
        {id: 'rating', label: this.translateService.instant('RATING'), iconSuffix: 'hourglass-outline', hide: true}
    ];
    
    isLoggedUserAllowedToPlan: boolean = false;
    isFetching: boolean = false;
    missionDetails: MissionDetails|null = null;
    storeCheckId: number|null = null;
    retailerLogo: RetailerLogo|null = null; 
            
    showFooterAction: boolean = false;
        
    showBtnEditOrder: boolean = false;
    showBtnEditStoreCheck: boolean = false;
    showBtnDuplicateStoreCheck: boolean = false;
    operation: Operation|null = null;   
    customerPlanningSettings: OperationCustomerPlanningSetting|null = null; 
    order: Order|null = null;
    orderDay: OrderDay;
    
    isFetchingDelete: boolean = false;
    isFetchingCancel: boolean = false;
    showBtnDeleteOrder: boolean = false;
    showBtnCancelStoreCheck: boolean = false;
    showBtnDeleteStoreCheck: boolean = false;
    toogleDeleteAllActivities: boolean = false;
    deleteOrderForm: FormGroup = new FormGroup({});
    
    showBtnRequestCancellationOrder: boolean = false;    
    requestCancellationForm: FormGroup = new FormGroup({
        comments: new FormControl(null)
    });
    
    showBtnAdditionalTimeOrder: boolean = false; 
    additionalTimes: Array<AdditionalTimes> = [];  
    additionalTimesForm: FormGroup = new FormGroup({});
    
    constructor(        
        public translateService: TranslateService,
        private toastController: ToastController,
        private alertController: AlertController,
        private marvModalService: MarvModalService,
        private loggedUserService: LoggedUserService,
        private operationService: OperationService,
        private orderService: OrderService,
        private storeCheckService: StoreCheckService,
        public missionTypeService: MissionTypeService
    ) { }

    ngOnInit() {
        this.isLoggedUserAllowedToPlan = (this.loggedUserService.loggedUser?.customerPlanningAllowed === true);
    }

    async ngOnChanges(changes: SimpleChanges) {
        if(changes['missionId'] && changes['missionId'].currentValue){
            this.getOrderDetails();
        }
        
        if(changes['tabSelected'] && changes['tabSelected'].currentValue){
            this.tabsMission.forEach(t => {
                t.defaultSelected = t.id === this.tabSelected;                
            });
        }
    }
    
    async getOrderDetails(){
        
        this.retailerLogo = null;
        this.missionDetails = null;
        this.storeCheckId = null;
        this.operation = null;   
        this.customerPlanningSettings = null;
        this.order = null;
        this.showFooterAction = false;
        this.showBtnEditOrder = false;
        this.showBtnEditStoreCheck = false;
        this.showBtnDuplicateStoreCheck = false;
        this.showBtnDeleteOrder = false;
        this.showBtnCancelStoreCheck = false;
        this.showBtnDeleteStoreCheck = false;
        this.showBtnRequestCancellationOrder = false;
        this.showBtnAdditionalTimeOrder = false;
        this.additionalTimes = [];
        this.additionalTimesForm = new FormGroup({});        
        this.isFetching = true;
        
        let opCocaVitrine: OperationCard|null = null;
        
        if(this.source === 'loga'){
            let order: Order|null = await this.orderService.loadOrder(this.missionId);
            if(order){
                
                this.order = order;
                this.customerPlanningSettings = OperationService.OperationsSettings.get(order.operation.id) || null;
                let tabPcheck = await this.orderService.loadPcheck(this.missionId);
                this.missionDetails = MissionDetails.FromOrder(this.customerPlanningSettings, order, tabPcheck, this.activityId);
                
                // A MODIFIER - Ajout de la saisie des vitrines en dur
                opCocaVitrine = OperationService.Operations.find(o => o.id === this.missionDetails?.operation?.id && this.missionDetails?.operation?.id === 2028) || null;                
                
                let deleteOrderForm: FormGroup = new FormGroup({});                
                
                let startDate: number|null = null;
                let endDate: number|null = null;
                let today = MarvDateService.GetToday();
                this.missionDetails.activities.forEach(a => {
                    if(startDate === null || a.day < startDate){
                        startDate = a.day;
                    }
                    if(endDate === null || endDate < a.day){
                        endDate = a.day;
                    }
                    
                    deleteOrderForm.addControl('activity_'+a.id, new FormControl(this.activityId === a.id), {emitEvent: false});        
                    
                });
                
                await this.getAdditionalWorkingTime();
                
                this.showBtnEditOrder = this.isLoggedUserAllowedToPlan && this.missionDetails.customerEditPermissionAllowed;
                if(this.showBtnEditOrder){
                    let operation = await this.operationService.loadOperation(order.operation.id);
                    if(operation){
                        this.operation = operation;                   
                    }
                }
                                
                this.deleteOrderForm = deleteOrderForm;
                
                this.showBtnDeleteOrder = this.isLoggedUserAllowedToPlan && this.missionDetails.customerDeletePermissionAllowed;
                this.showBtnRequestCancellationOrder = this.isLoggedUserAllowedToPlan && (this.missionDetails.customerDeleteRequestPermissionAllowed && endDate !== null && today <= endDate);                   
                this.showBtnAdditionalTimeOrder = this.isLoggedUserAllowedToPlan && this.additionalTimes.length > 0;
                
                this.showFooterAction = this.missionDetails.cancellationRequest === null 
                    && (this.showBtnEditOrder || this.showBtnDeleteOrder || this.showBtnRequestCancellationOrder || this.showBtnAdditionalTimeOrder)
            }
        }
        else if(this.source === 'lgg'){
            let storeCheck: StoreCheck|null = await this.storeCheckService.loadStoreCheck(this.operationId, this.missionId);
            if(storeCheck){
                this.missionDetails = MissionDetails.FromStoreCheck(storeCheck, this.activityId);   
                if(this.missionDetails){   
                    this.storeCheckId = this.missionDetails.missionId;
                    this.showBtnEditStoreCheck = this.missionDetails.customerEditPermissionAllowed;
                    this.showBtnDuplicateStoreCheck = this.missionDetails.customerDuplicatePermissionAllowed;
                    this.showBtnCancelStoreCheck = this.missionDetails.customerDeleteRequestPermissionAllowed && !this.missionDetails.hasCoachingSession && !this.missionDetails.hasMysteryCheck;
                    this.showBtnDeleteStoreCheck = this.missionDetails.customerDeletePermissionAllowed && !this.missionDetails.hasCoachingSession && !this.missionDetails.hasMysteryCheck;
                    this.showFooterAction = this.showBtnEditStoreCheck || this.showBtnDuplicateStoreCheck || this.showBtnCancelStoreCheck || this.showBtnDeleteStoreCheck;   
                }  
            }
        }  
        
        let tabAdditionalsDatas = this.tabsMission.find(t => t.id === 'additional-data');
        if(tabAdditionalsDatas){
            tabAdditionalsDatas.hide = (opCocaVitrine === null);
        }

        if(this.missionDetails){                
            if(this.missionDetails.retailer?.name){
                this.retailerLogo = new RetailerLogo(this.missionDetails.retailer.name);
            }  
            let tabMissions = this.tabsMission.find(t => t.id === 'activities');
            if(tabMissions){                    
                tabMissions.countItems = this.missionDetails.activities.length;
            }
            let tabRating = this.tabsMission.find(t => t.id === 'rating');
            if(tabRating){
               tabRating.hide = !this.missionDetails.showRatingTab;
                if(this.missionDetails.customerFeedback){                    
                    tabRating.iconSuffix = 'star';
                }
            }
        }
        
        this.isFetching = false;
    }
    
    async getAdditionalWorkingTime(){       
        this.additionalTimes = [];
        this.additionalTimesForm = new FormGroup({}); 
        if(this.missionDetails?.missionId){
            this.additionalTimes = await this.orderService.loadAdditionalWorkingTime(this.missionDetails.missionId);
            for(let at of this.additionalTimes){
                this.additionalTimesForm.addControl('nb_hours_'+at.day, new FormControl(at.nbHours || 0, [Validators.required]), {emitEvent: false});
                this.additionalTimesForm.addControl('nb_minutes_'+at.day, new FormControl(at.nbMinutes || 0, [Validators.required]), {emitEvent: false});
            }
            
        }
    }
    
    async editOrder(){       
        if(this.order && this.customerPlanningSettings){
            this.orderDay = OrderDay.FromOrder(this.order, this.customerPlanningSettings);
            this.drawerEditDay.open();       
        }        
    }
        
    onDidDismissDrawerEditOrder($event: any){    
        
    }
    
    async onChangeOrderDay(orderDay: OrderDay|null){    
        if(orderDay){
            this.activityId = null;
            this.getOrderDetails();
            this.onUpdateMission.emit(true);
        }
        this.drawerEditDay.close(true);
    }
    
    openDeleteOrderModal() {
        this.toogleDeleteAllActivities = false;
        this.marvModalService.open(this.modalDeleteOrder);       
    }
    
    async canDismissDeleteOrderModal(data?: any, role?: string) {
        return role !== 'gesture';
    }
    
    onDidDismissDeleteOrderModal($event: any){
        this.deleteOrderForm.reset();
        if(this.activityId && this.deleteOrderForm.controls['activity_'+this.activityId]){
            this.deleteOrderForm.controls['activity_'+this.activityId].setValue(true);
        }
        this.marvModalService.onDidDismiss($event);
    }
    
    closeDeleteOrderModal(){
        this.modalDeleteOrder.dismiss();
    }
    
    checkDeleteOrder(){
        if(this.missionDetails?.activities.length === 1){
            this.confirmDeleteOrder();
        }
        else{
            this.openDeleteOrderModal();
        }
    }
    
    deleteAllActivities(){
        if(this.missionDetails){
            this.toogleDeleteAllActivities = !this.toogleDeleteAllActivities;
            for(let a of this.missionDetails.activities){
                this.deleteOrderForm.controls['activity_'+a.id].setValue((a.id != this.activityId ? this.toogleDeleteAllActivities : true));
            }
        }
    }
    
    async deleteOrderOrActivity(){   
        if(this.missionDetails){
            this.isFetchingDelete = true;
            let activitiesIdToDelete: Array<number> = [];
            for(let a of this.missionDetails.activities){
                if(this.deleteOrderForm.controls['activity_'+a.id].value){
                    activitiesIdToDelete.push(a.id);
                }
            }
            if(activitiesIdToDelete.length === this.missionDetails.activities.length){
                await this.orderService.deleteOrder(this.missionDetails.missionId);                
            }
            else{
                for(let id of activitiesIdToDelete){
                    await this.orderService.deleteActivity(id);
                }    
            }
            this.isFetchingDelete = false;
            this.onDeleteMission.emit(true);
        }
    }
    
    async confirmDeleteOrder(){        
        const alert = await this.alertController.create({
            message: this.translateService.instant('MSG_CONFIRM_DELETE_ORDER'),
            buttons: [{
                    text: this.translateService.instant('VALIDATE'),
                    role: 'confirm',
                    handler: async () => {
                        if(this.missionDetails){
                            this.isFetchingDelete = true;
                            await this.orderService.deleteOrder(this.missionDetails.missionId);
                            this.isFetchingDelete = false;
                            this.onDeleteMission.emit(true);
                        }
                    }
                },
                {
                    text: this.translateService.instant('CANCEL'),
                    role: 'cancel'
                }
            ],
        })
        await alert.present();             
    }
    
    openRequestCancellationModal() {
        this.marvModalService.open(this.modalRequestCancellation);       
    }
    
    async canDismissRequestCancellationModal(data?: any, role?: string) {
        return role !== 'gesture';
    }
    
    onDidDismissRequestCancellationModal($event: any){
        this.requestCancellationForm.reset();        
        this.marvModalService.onDidDismiss($event);
    }
    
    closeRequestCancellationModal(){
        this.modalRequestCancellation.dismiss();
    }
    
    async sendRequestCancellation(){
        if(this.missionDetails?.missionId && this.showBtnRequestCancellationOrder){
            this.isFetchingCancel = true;
            await this.orderService.cancelOrder(this.missionDetails.missionId, this.requestCancellationForm.controls['comments'].value);
            this.isFetchingCancel = false;
            const toast = await this.toastController.create({
                message: this.translateService.instant('MSG_SUCCESS_REQUEST_CANCELLATION'),
                duration: 6000,
                position: 'top',
                color: 'success'
            });
            await toast.present();
            this.closeRequestCancellationModal();
            await this.getOrderDetails();
        }
    }
    
    openAdditionalTimeModal() {
        this.marvModalService.open(this.modalAdditionalTime);
    }
    
    async canDismissAdditionalTimeModal(data?: any, role?: string) {
        return role !== 'gesture';
    }
    
    async onDidDismissAdditionalTimeModal($event: any){       
        await this.getAdditionalWorkingTime();
        this.marvModalService.onDidDismiss($event);
    }
    
    closeAdditionalTimeModal(){
        this.modalAdditionalTime.dismiss();
    }
    
    async saveAdditionalTimes(){
        if(this.missionDetails?.missionId && this.showBtnAdditionalTimeOrder){
            if(this.additionalTimesForm.valid){
                let additionalTimes = this.additionalTimes.slice();
                for(let at of additionalTimes){
                    at.nbHours = this.additionalTimesForm.controls['nb_hours_'+at.day].value;
                    at.nbMinutes = this.additionalTimesForm.controls['nb_minutes_'+at.day].value;
                }
                await this.orderService.saveAdditionalWorkingTime(this.missionDetails.missionId, additionalTimes);
                const toast = await this.toastController.create({
                    message: this.translateService.instant('MSG_SUCCESS_SAVE_ADDITIONAL_TIMES'),
                    duration: 6000,
                    position: 'top',
                    color: 'success'
                });
                await toast.present();
                this.closeAdditionalTimeModal();
            }
            else{
                for (var controlName in this.additionalTimesForm.controls) {
                    if(!this.additionalTimesForm.controls[controlName].valid) {
                        this.additionalTimesForm.controls[controlName].markAsTouched();
                    }
                }
            }
        }
    }
    
    editStoreCheck(){
        this.drawerPermanentStoreCheck.open();
    }
    
    async onDidDismissDrawerPermanentStoreCheck($event: any){
        
    }    
    
    async onChangeStoreCheck(event: StoreCheckChangeEvent) {
        this.drawerPermanentStoreCheck.close(true);        
        if(event.statusUpdate){
            this.getOrderDetails();
            this.onUpdateMission.emit(true);
        }   
        else{
            let storeCheckId = this.storeCheckId;
            this.storeCheckId = null;
            setTimeout(() => {
                this.storeCheckId = storeCheckId;
            }, 10);
        }     
    }       
           
    duplicateStoreCheck(){
        this.drawerPermanentStoreCheckDuplicate.open();
    }
    
    async onDidDismissDrawerPermanentStoreCheckDuplicate($event: any){
        
    }    
    
    async onDuplicateStoreCheck(newStoreCheckId: number|null) {
        this.drawerPermanentStoreCheckDuplicate.close(true);        
        if(newStoreCheckId){
            this.missionId = newStoreCheckId;
            this.activityId = newStoreCheckId;
            await this.getOrderDetails();
        }
        else{
            let storeCheckId = this.storeCheckId;
            this.storeCheckId = null;
            setTimeout(() => {
                this.storeCheckId = storeCheckId;
            }, 10);
        }    
    }
    
    async confirmCancelStoreCheck(){
        const alert = await this.alertController.create({
            message: this.translateService.instant('MSG_CONFIRM_CANCEL_STORECHECK'),
            buttons: [{
                    text: this.translateService.instant('VALIDATE'),
                    role: 'confirm',
                    handler: async () => {
                        this.cancelStoreCheck();
                    }
                },
                {
                    text: this.translateService.instant('CANCEL'),
                    role: 'cancel'
                }
            ],
        })
        await alert.present();             
    }
    
    async cancelStoreCheck(){
        if(this.missionDetails){
            this.isFetchingCancel = true;
            await this.storeCheckService.cancelStoreCheck(this.missionDetails.operation.id, this.missionDetails.missionId);
            this.isFetchingCancel = false;
            const toast = await this.toastController.create({
                message: this.translateService.instant('MSG_SUCCESS_CANCEL_STORECHECK'),
                duration: 6000,
                position: 'top',
                color: 'success'
            });
            await toast.present();
            await this.getOrderDetails();
        }
    }
    
    async confirmDeleteStoreCheck(){
        const alert = await this.alertController.create({
            message: this.translateService.instant('MSG_CONFIRM_DELETE_STORECHECK'),
            buttons: [{
                    text: this.translateService.instant('VALIDATE'),
                    role: 'confirm',
                    handler: async () => {
                        this.deleteStoreCheck();
                    }
                },
                {
                    text: this.translateService.instant('CANCEL'),
                    role: 'cancel'
                }
            ],
        })
        await alert.present();          
    }
    
    async deleteStoreCheck(){
        if(this.missionDetails){
            this.isFetchingDelete = true;
            await this.storeCheckService.deleteStoreCheck(this.missionDetails.operation.id, this.missionDetails.missionId);
            this.isFetchingDelete = false;
            const toast = await this.toastController.create({
                message: this.translateService.instant('MSG_SUCCESS_DELETE_STORECHECK'),
                duration: 6000,
                position: 'top',
                color: 'success'
            });
            await toast.present();
            this.onDeleteMission.emit(true);
        }
    }
    
    async onUpdateInfos(status: boolean) {
        if(status){
            this.getOrderDetails();
            this.onUpdateMission.emit(true);
        } 
    }
}
